import React from 'react';
import { Grid, Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import UndoIcon from '@mui/icons-material/Undo';
import { StatCard } from './StatCard';

interface FinancialDashboardProps {
    data: any;
}

const formatCurrency = (amount: string | number, currency: string = 'EUR'): string => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: currency,
    }).format(num || 0);
};

export const FinancialDashboard: React.FC<FinancialDashboardProps> = ({ data }) => {
    if (!data) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="textSecondary">Keine Daten verfügbar</Typography>
            </Box>
        );
    }

    const { summary, trends } = data;
    const currency = summary?.currency || 'EUR';

    return (
        <Box sx={{ px: 2 }}>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Gesamtumsatz"
                        value={formatCurrency(summary?.total_revenue || 0, currency)}
                        icon={<AttachMoneyIcon sx={{ fontSize: 48 }} />}
                        color="success"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Bestellungen"
                        value={summary?.total_orders || 0}
                        subtitle={`${summary?.successful_orders || 0} erfolgreich`}
                        icon={<ShoppingCartIcon sx={{ fontSize: 48 }} />}
                        color="primary"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Ø Bestellwert"
                        value={formatCurrency(summary?.avg_order_value || 0, currency)}
                        icon={<TrendingUpIcon sx={{ fontSize: 48 }} />}
                        color="info"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Erstattungen"
                        value={formatCurrency(summary?.total_refunds || 0, currency)}
                        subtitle={`${summary?.refunded_orders || 0} Bestellungen`}
                        icon={<UndoIcon sx={{ fontSize: 48 }} />}
                        color="warning"
                    />
                </Grid>
            </Grid>

            {/* Revenue Trend Chart */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                    Umsatztrend
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={trends || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" />
                        <YAxis />
                        <Tooltip
                            formatter={(value: any) => formatCurrency(value, currency)}
                        />
                        <Legend />
                        <Area
                            type="monotone"
                            dataKey="revenue"
                            name="Umsatz"
                            stroke="#4caf50"
                            fill="#4caf50"
                            fillOpacity={0.3}
                        />
                        <Area
                            type="monotone"
                            dataKey="refunds"
                            name="Erstattungen"
                            stroke="#f44336"
                            fill="#f44336"
                            fillOpacity={0.3}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </Paper>

            {/* Detailed Table */}
            {trends && trends.length > 0 && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Detaillierte Übersicht
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Zeitraum</TableCell>
                                    <TableCell align="right">Bestellungen</TableCell>
                                    <TableCell align="right">Umsatz</TableCell>
                                    <TableCell align="right">Erstattungen</TableCell>
                                    <TableCell align="right">Netto</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {trends.map((row: any, index: number) => {
                                    const net = (parseFloat(row.revenue) || 0) - (parseFloat(row.refunds) || 0);
                                    return (
                                        <TableRow key={index}>
                                            <TableCell>{row.period}</TableCell>
                                            <TableCell align="right">{row.orders || 0}</TableCell>
                                            <TableCell align="right" sx={{ color: '#4caf50' }}>
                                                {formatCurrency(row.revenue || 0, currency)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: '#f44336' }}>
                                                {formatCurrency(row.refunds || 0, currency)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                {formatCurrency(net, currency)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Order Status Summary */}
            <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Bestellstatus
                </Typography>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                            <Typography variant="h4" sx={{ color: '#4caf50' }}>
                                {summary?.successful_orders || 0}
                            </Typography>
                            <Typography color="textSecondary">Erfolgreich</Typography>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                            <Typography variant="h4" sx={{ color: '#ff9800' }}>
                                {summary?.cancelled_orders || 0}
                            </Typography>
                            <Typography color="textSecondary">Storniert</Typography>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                            <Typography variant="h4" sx={{ color: '#f44336' }}>
                                {summary?.refunded_orders || 0}
                            </Typography>
                            <Typography color="textSecondary">Erstattet</Typography>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                            <Typography variant="h4" color="primary">
                                {summary?.total_orders || 0}
                            </Typography>
                            <Typography color="textSecondary">Gesamt</Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

export default FinancialDashboard;
